import {
	INCOMING_RING,
	OUTGOING_RING,
	PRODUCTION_MODE,
	ZEGOCLOUD_APP_ID,
	ZEGOCLOUD_RESOURCESID,
	ZEGOCLOUD_SERVERSECRET,
} from 'constants';
import { useEffect } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { loginZIM, useGenerateTokenMutation } from 'services';
import useAuthStore from 'store/auth';
import useCommonStore, { setModalConfig } from 'store/common';
import useZegoCloudStore, { setZimUserDetails, setZpInstance } from 'store/zegoCloud';
import useTranslate from './useTranslate';

const useZegoCloud = () => {
	const { user: authUser } = useAuthStore((state) => state.userInfo ?? {});
	const { zpInstance } = useZegoCloudStore((state) => state ?? {});
	const { hideCommonModal } = useCommonStore((state) => state);
	const { mutateAsync: GenerateTokenMutation } = useGenerateTokenMutation();
	const { translate } = useTranslate();

	// Lazy load heavy dependencies
	const lazyLoadZego = async () => {
		const { ZegoUIKitPrebuilt } = await import('@zegocloud/zego-uikit-prebuilt');
		const { ZIM } = await import('zego-zim-web');
		return { ZegoUIKitPrebuilt, ZIM };
	};

	// Preload resources when hook initializes
	useEffect(() => {
		if (authUser?.userId) {
			lazyLoadZego().catch(console.error);
		}
	}, [authUser?.userId]);

	const createZegoLogin = async () => {
		if (!authUser?.userId) return;

		try {
			const tokenResponse = await GenerateTokenMutation({ userID: authUser.userId });
			if (tokenResponse?.success && tokenResponse.data) {
				const zimUser = await loginZIM(
					authUser.userId.toString(),
					`userName_${authUser.userId}`,
					tokenResponse.data,
				);
				if (zimUser) {
					await handleZimUserLogin(zimUser, tokenResponse.data);
				}
			}
		} catch (error) {
			console.error('Error during Zego login process:', error);
		}
	};

	const handleZimUserLogin = async (zimUser: any, zegoToken: any) => {
		setZimUserDetails(zimUser);
		sessionStorage.setItem('zimUser', JSON.stringify(zimUser));
		await generateKitToken(zimUser, zegoToken);
	};

	const generateKitToken = async (zimUser: any, zegoToken: any) => {
		if (!zimUser) return;

		try {
			const { ZegoUIKitPrebuilt, ZIM } = await lazyLoadZego();
			const userID = zimUser.userID;
			const userName = authUser?.firstName || authUser?.companyName;

			const token = PRODUCTION_MODE
				? ZegoUIKitPrebuilt.generateKitTokenForProduction(
						Number(ZEGOCLOUD_APP_ID),
						zegoToken,
						'',
						userID,
						userName,
					)
				: ZegoUIKitPrebuilt.generateKitTokenForTest(
						Number(ZEGOCLOUD_APP_ID),
						ZEGOCLOUD_SERVERSECRET,
						'',
						userID,
						userName,
						36000,
					);

			const zp = ZegoUIKitPrebuilt.create(token);
			setZpInstance(zp);
			configureZpInstance(zp, ZIM);
		} catch (error) {
			console.error('Error generating Kit token or setting up ZP instance:', error);
		}
	};

	const configureZpInstance = (zp: any, ZIM: any) => {
		zp.addPlugins({ ZIM });

		zp.setCallInvitationConfig({
			enableCustomCallInvitationWaitingPage: true,
			enableCustomCallInvitationDialog: true,
			enableNotifyWhenAppRunningInBackgroundOrQuit: true,
			ringtoneConfig: {
				incomingCallUrl: INCOMING_RING,
				outgoingCallUrl: OUTGOING_RING,
			},
			onWaitingPageWhenSending: handleWaitingPageOnSending,
			onConfirmDialogWhenReceiving: handleConfirmationDialog,
			onSetRoomConfigBeforeJoining: () => {
				setModalConfig({ visible: false, type: 'waitingPage' });
				return {
					showRoomDetailsButton: false,
				};
			},
			onCallInvitationEnded: (reason: any) => handleCallInvitationEnded(reason),
		});
	};

	const handleWaitingPageOnSending = (callType: number, callees: any[], cancel: () => void) => {
		setModalConfig({
			visible: true,
			type: 'waitingPage',
			data: {
				name: callees[0]?.userName,
				message: `${callType === 1 ? 'Video' : 'Audio'} Calling`,
				cancelBtn: 'Cancel',
				onCancelBtnClick: () => {
					cancel();
					hideCommonModal();
				},
			},
			props: {
				centered: true,
				dialogClassName: 'dialog-zego-cloud',
				size: 'sm',
				fullscreen: isMobileOnly,
			},
		});
	};

	const handleConfirmationDialog = (
		callType: number,
		caller: any,
		refuse: () => void,
		accept: () => void,
	) => {
		setModalConfig({
			visible: true,
			type: 'waitingPage',
			data: {
				name: caller?.userName,
				message: `Incoming ${callType === 1 ? 'Video' : 'Audio'} Call`,
				declineBtn: 'Decline',
				acceptBtn: 'Accept',
				onDeclineBtnClick: () => {
					hideCommonModal();
					refuse();
				},
				onAcceptBtnClick: () => {
					hideCommonModal();
					accept();
				},
			},
			props: {
				centered: true,
				dialogClassName: 'dialog-zego-cloud',
				size: 'sm',
				fullscreen: isMobileOnly,
			},
		});
	};

	const handleCallInvitationEnded = async (reason: string) => {
		setModalConfig({ visible: false, type: 'waitingPage' });
	};

	const invite = async (callType: number, receiverDetails: any) => {
		if (!receiverDetails?.id) {
			console.error('Invalid receiver details provided:', receiverDetails);
			return;
		}

		try {
			// Initialize if not already done
			if (!zpInstance) {
				await createZegoLogin();
				if (!zpInstance) throw new Error('Failed to initialize ZP instance');
			}

			const targetUser = {
				userID: receiverDetails.id.toString(),
				userName: receiverDetails.name,
			};

			const res = await zpInstance.sendCallInvitation({
				callees: [targetUser],
				callType: callType || 0,
				timeout: 30,
				notificationConfig: {
					resourcesID: ZEGOCLOUD_RESOURCESID,
					title: translate('Common.callInvitation'),
					message: translate('Common.incomingCall'),
				},
			});
			console.warn('Call Invitation sent successfully:', res);
		} catch (err) {
			console.error('Failed to send call invitation:', err.message || JSON.stringify(err));
		}
	};

	return { invite, createZegoLogin };
};

export default useZegoCloud;
